import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [sortBy, setSortBy] = useState('latest');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [postRefs, postInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  // Sample blog posts data with accessible placeholder images and updated dates to May 2025
  const blogPosts = [
    {
      id: 1,
      title: 'The Future of AI in Business Operations',
      excerpt: 'Exploring how artificial intelligence is revolutionizing how businesses operate in 2025 and beyond.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80',
      category: 'AI',
      date: '2025-05-15',
      author: {
        name: 'Alex Johnson',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
      },
      tags: ['AI', 'Business', 'Technology']
    },
    {
      id: 2,
      title: 'Web Development Trends for 2025',
      excerpt: 'A comprehensive look at the web development technologies and approaches that will dominate this year.',
      image: 'https://images.unsplash.com/photo-1550439062-609e1531270e?q=80&w=1770&auto=format&fit=crop',
      category: 'Web',
      date: '2025-05-08',
      author: {
        name: 'Maya Patel',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
      },
      tags: ['Web Development', 'Frontend', 'Design']
    },
    {
      id: 3,
      title: 'Building Scalable SaaS Applications',
      excerpt: 'Key architectural decisions and best practices for creating SaaS products that can grow with your user base.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1615&auto=format&fit=crop',
      category: 'SaaS',
      date: '2025-05-20',
      author: {
        name: 'Chris Wilson',
        avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
      },
      tags: ['SaaS', 'Architecture', 'Scalability']
    },
    {
      id: 4,
      title: 'Cybersecurity Essentials for Small Businesses',
      excerpt: 'Practical security measures that small businesses can implement to protect their digital assets.',
      image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?q=80&w=1770&auto=format&fit=crop',
      category: 'Security',
      date: '2025-05-03',
      author: {
        name: 'Sarah Chen',
        avatar: 'https://randomuser.me/api/portraits/women/23.jpg'
      },
      tags: ['Security', 'Small Business', 'Protection']
    }
  ];

  // Filter and sort blog posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = category === 'All' || post.category === category;
    
    return matchesSearch && matchesCategory;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortBy === 'latest') {
      return new Date(b.date) - new Date(a.date);
    } else {
      // Alphabetical
      return a.title.localeCompare(b.title);
    }
  });

  // Get category color class
  const getCategoryColorClass = (category) => {
    const categories = {
      'AI': 'category-ai',
      'Web': 'category-web',
      'SaaS': 'category-saas',
      'Security': 'category-security',
      'Branding': 'category-branding',
      'Digital': 'category-digital'
    };
    
    return categories[category] || 'category-web';
  };

  return (
    <motion.div 
      className="blog-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Blog Hero Section - Updated to match Services Page hero styling */}
      <motion.section 
        className="services-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        ref={ref}
      >
        <div className="hero-background">
          <div className="neural-network"></div>
        </div>
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.h1
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="gradient-text"
          >
            Our Blog
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Insights, news, and expertise from Oasis Logic
          </motion.p>
          <motion.div 
            className="hero-buttons"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <motion.button 
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Subscribe
            </motion.button>
            <motion.button 
              className="outline-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Latest Articles
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.section>

      <div className="blog-main-content">
        {/* Blog Filters & Search */}
        <motion.div 
          className="blog-controls glass-morphism"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="controls-container">
            <input 
              type="text" 
              className="blog-search" 
              placeholder="Search articles..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="blog-filters">
              <select 
                className="category-filter"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="All">All Categories</option>
                <option value="AI">AI</option>
                <option value="Web">Web Development</option>
                <option value="SaaS">SaaS</option>
                <option value="Security">Security</option>
                <option value="Branding">Branding</option>
                <option value="Digital">Digital Marketing</option>
              </select>
              <select 
                className="sort-filter"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="latest">Latest</option>
                <option value="alphabetical">A-Z</option>
              </select>
            </div>
          </div>
          <div className="futuristic-badges">
            <div className="topic-badge">Artificial Intelligence</div>
            <div className="topic-badge">Web Development</div>
            <div className="topic-badge">Tech Insights</div>
            <div className="topic-badge">Business Growth</div>
          </div>
        </motion.div>

        {/* Blog Posts Grid */}
        <motion.section 
          ref={postRefs}
          className="blog-posts-container"
          variants={containerVariants}
          initial="hidden"
          animate={postInView ? "visible" : "hidden"}
        >
          {sortedPosts.length === 0 ? (
            <motion.div 
              className="no-results glass-morphism"
              variants={itemVariants}
            >
              <h3>No matching posts found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </motion.div>
          ) : (
            <div className="blog-card-container">
              {sortedPosts.map((post) => (
                <BlogCard key={post.id} post={post} variants={itemVariants} getCategoryColorClass={getCategoryColorClass} />
              ))}
            </div>
          )}
        </motion.section>

        {/* Pagination */}
        <motion.div 
          className="pagination"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <button className="pagination-button active">1</button>
          <button className="pagination-button">2</button>
          <button className="pagination-button">3</button>
          <button className="pagination-button">Next »</button>
        </motion.div>

        {/* Subscribe Section */}
        <motion.section 
          className="subscribe-section glass-morphism"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Subscribe to Our Newsletter</h2>
          <p>Stay updated with our latest articles, insights, and company news</p>
          <div className="subscribe-form">
            <input type="email" placeholder="Enter your email address" />
            <button className="cta-button blog-cta-button">Subscribe</button>
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

// Blog Card Component with improved image handling
const BlogCard = ({ post, variants, getCategoryColorClass }) => {
  // Define multiple reliable fallbacks
  const fallbackImageUrl = "https://via.placeholder.com/800x450/1e293b/38bdf8?text=Oasis+Logic+Blog";
  const fallbackAvatarUrl = "https://via.placeholder.com/40/1e293b/38bdf8?text=A";
  
  const [imageError, setImageError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // Handle image loading errors
  const handleImageError = () => {
    console.log("Image failed to load:", post.image);
    setImageError(true);
  };
  
  // Handle avatar loading errors
  const handleAvatarError = () => {
    console.log("Avatar failed to load:", post.author.avatar);
    setAvatarError(true);
  };

  return (
    <motion.div className="blog-card" variants={variants}>
      <div className="ai-scan-effect blog-scan"></div>
      <div className="blog-card-image">
        <img 
          src={imageError ? fallbackImageUrl : post.image}
          alt={post.title}
          onError={handleImageError}
        />
        <div className="blog-card-image-overlay"></div>
        <div className={`blog-card-category ${getCategoryColorClass(post.category)}`}>
          {post.category}
        </div>
      </div>
      <div className="blog-card-content">
        <div className="blog-card-meta">
          <div className="blog-card-meta-item">
            <span className="meta-icon">📅</span>
            {new Date(post.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </div>
        </div>
        <h3 className="blog-card-title">{post.title}</h3>
        <p className="blog-card-excerpt">{post.excerpt}</p>
        <div className="blog-card-tags">
          {post.tags.map((tag, index) => (
            <span key={index} className="blog-card-tag">{tag}</span>
          ))}
        </div>
        <div className="blog-card-footer">
          <div className="blog-card-author">
            <img 
              src={avatarError ? fallbackAvatarUrl : post.author.avatar}
              alt={post.author.name}
              className="blog-card-avatar"
              onError={handleAvatarError}
            />
            <span className="blog-card-author-name">{post.author.name}</span>
          </div>
          <a href={`/blog/${post.id}`} className="blog-card-readmore">
            Read More <span>→</span>
          </a>
        </div>
        <div className="blog-card-circuit">
          <div className="blog-card-node"></div>
          <div className="blog-card-line"></div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogPage;